import React, { useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
    AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
    Plus, Pencil, Trash2, FileText, PlayCircle, Presentation,
    HelpCircle, FileDown, Sparkles, Bot, GripVertical, Wand2, Film,
} from "lucide-react";
import { toast } from "sonner";
import {
    DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
    SortableContext, useSortable, arrayMove,
    verticalListSortingStrategy, sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import LessonDialog from "./LessonDialog";
import ChapterDialog from "./ChapterDialog";
import QuizDialog from "./QuizDialog";
import OutlineGenerateDialog from "@/components/wizard/OutlineGenerateDialog";
import BulkImportPdfDialog from "./BulkImportPdfDialog";
import AiSplitPdfDialog from "./AiSplitPdfDialog";
import RenderAllVideosDialog from "./RenderAllVideosDialog";
import RenderAllPptxDialog from "./RenderAllPptxDialog";
import CombinedChapterPptxButton from "./CombinedChapterPptxButton";
import ConvertPdfDialog from "./ConvertPdfDialog";
import ChapterConvertAllDialog from "./ChapterConvertAllDialog";

const TYPE_ICON = {
    text: FileText,
    video: PlayCircle,
    presentation: Presentation,
};

// ---------- Drag handle ----------
function DragHandle({ listeners, attributes, testId, label }) {
    return (
        <button
            type="button"
            className="p-1.5 -ml-1 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing touch-none"
            data-testid={testId}
            aria-label={label}
            {...listeners}
            {...attributes}
        >
            <GripVertical className="h-4 w-4" />
        </button>
    );
}

// ---------- Sortable lesson row ----------
function SortableLesson({ lesson, index, chapterId, chapterTitle, onEdit, onDelete, onGenSlides, onConvert, slidesLoading }) {
    const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
        id: lesson.id,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
    };
    const Icon = TYPE_ICON[lesson.content_type] || FileText;
    const hasPdfAttach = (lesson.attachments || []).some(
        (a) => (a?.content_type || "").toLowerCase() === "application/pdf",
    );
    const isPdfOnly = lesson.content_type === "pdf-attachment";
    return (
        <div
            ref={setNodeRef}
            style={style}
            className="flex items-center gap-3 p-3 border border-border rounded-sm bg-secondary/20 hover:bg-secondary/40 transition-colors"
            data-testid={`lesson-${lesson.id}`}
        >
            <DragHandle
                listeners={listeners}
                attributes={attributes}
                testId={`lesson-drag-${lesson.id}`}
                label="Drag to reorder lesson"
            />
            <div className="text-xs text-muted-foreground w-6 text-right">{index + 1}.</div>
            <Icon className="h-4 w-4 text-primary flex-shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground truncate">{lesson.title}</div>
                <div className="text-xs text-muted-foreground capitalize">
                    {isPdfOnly ? "PDF attached" : lesson.content_type}
                    {lesson.reading_time_minutes ? ` · ${lesson.reading_time_minutes} min read` : ""}
                    {lesson.converted_mode ? ` · converted (${lesson.converted_mode})` : ""}
                    {lesson.slides_id && " · slides ready"}
                    {!lesson.approved && " · pending review"}
                </div>
            </div>
            <div className="flex items-center gap-1">
                {hasPdfAttach && (
                    <Button
                        size="sm"
                        variant={isPdfOnly ? "default" : "outline"}
                        onClick={() => onConvert?.(lesson)}
                        data-testid={`lesson-convert-${lesson.id}`}
                        className="h-8 gap-1 text-xs"
                        title={
                            isPdfOnly
                                ? "Convert this PDF into a readable lesson"
                                : "Re-convert PDF (replaces current content)"
                        }
                    >
                        <Sparkles className="h-3 w-3" />
                        Convert PDF
                    </Button>
                )}
                {lesson.slides_id && (
                    <Button
                        size="icon"
                        variant="ghost"
                        asChild
                        data-testid={`lesson-view-slides-${lesson.id}`}
                        aria-label="Download slides"
                    >
                        <a
                            href={`${API}/slides/${lesson.slides_id}/download?auth=${localStorage.getItem("token")}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <FileDown className="h-4 w-4" />
                        </a>
                    </Button>
                )}
                <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => onGenSlides(lesson.id)}
                    disabled={slidesLoading[lesson.id]}
                    data-testid={`lesson-gen-slides-${lesson.id}`}
                    aria-label="Generate slides"
                >
                    <Presentation className="h-4 w-4" />
                </Button>
                <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => onEdit(chapterId, lesson, chapterTitle)}
                    data-testid={`lesson-edit-${lesson.id}`}
                    aria-label="Edit lesson"
                >
                    <Pencil className="h-4 w-4" />
                </Button>
                <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => onDelete(lesson)}
                    data-testid={`lesson-del-${lesson.id}`}
                    aria-label="Delete lesson"
                >
                    <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
            </div>
        </div>
    );
}

// ---------- Sortable chapter ----------
function SortableChapter({
    chapter, index, onEditChapter, onDeleteChapter, onNewLesson,
    onEditLesson, onDeleteLesson, onGenSlides, onEditQuiz, onBulkImport, onAiSplit, onConvertLesson,
    onConvertAllInChapter, onRenderAll, onPptxAll,
    slidesLoading, sensors, onReorderLessons,
}) {
    const lessons = chapter.lessons || [];
    const {
        setNodeRef, transform, transition, attributes, listeners, isDragging,
    } = useSortable({ id: chapter.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const handleLessonDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIdx = lessons.findIndex((l) => l.id === active.id);
        const newIdx = lessons.findIndex((l) => l.id === over.id);
        if (oldIdx < 0 || newIdx < 0) return;
        const newOrder = arrayMove(lessons, oldIdx, newIdx).map((l) => l.id);
        onReorderLessons(chapter.id, newOrder);
    };

    return (
        <div ref={setNodeRef} style={style}>
            <AccordionItem
                value={chapter.id}
                className="border border-border rounded-sm bg-card"
                data-testid={`chapter-${chapter.id}`}
            >
                <div className="flex items-center gap-1 px-2">
                    <DragHandle
                        listeners={listeners}
                        attributes={attributes}
                        testId={`chapter-drag-${chapter.id}`}
                        label="Drag to reorder chapter"
                    />
                    <AccordionTrigger className="flex-1 hover:no-underline py-4">
                        <div className="flex items-start gap-3 text-left">
                            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1.5">
                                Chapter {index + 1}
                            </div>
                            <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-display text-lg text-primary">
                                        {chapter.title}
                                    </span>
                                    {chapter.is_ai_generated && (
                                        <Badge className="bg-accent/15 text-accent border border-accent/30 gap-1">
                                            <Bot className="h-3 w-3" />
                                            AI draft
                                        </Badge>
                                    )}
                                </div>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                    {lessons.length} lesson{lessons.length === 1 ? "" : "s"}
                                </div>
                            </div>
                        </div>
                    </AccordionTrigger>
                    <div className="flex items-center gap-1">
                        <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => onEditChapter(chapter)}
                            data-testid={`chapter-edit-${chapter.id}`}
                            aria-label="Edit chapter"
                        >
                            <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => onDeleteChapter(chapter)}
                            data-testid={`chapter-del-${chapter.id}`}
                            aria-label="Delete chapter"
                        >
                            <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                    </div>
                </div>
                <AccordionContent className="px-4 pb-4">
                    {chapter.description && (
                        <p className="text-sm text-muted-foreground mb-3">
                            {chapter.description}
                        </p>
                    )}
                    <div className="flex items-center justify-end gap-2 flex-wrap mb-3 pb-3 border-b border-border">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onNewLesson(chapter.id, chapter.title)}
                            data-testid={`add-lesson-top-${chapter.id}`}
                        >
                            <Plus className="h-3 w-3 mr-1" />
                            Add lesson
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => onBulkImport(chapter.id, chapter.title)}
                            className="ai-shimmer gap-1.5"
                            data-testid={`bulk-import-top-${chapter.id}`}
                        >
                            <Sparkles className="h-3.5 w-3.5" />
                            <span className="text-xs font-semibold">
                                Import lessons from PDFs
                            </span>
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onAiSplit(chapter.id, chapter.title)}
                            className="gap-1.5 border-accent text-accent-foreground hover:bg-accent/15"
                            data-testid={`ai-split-top-${chapter.id}`}
                            title="Upload ONE chapter PDF — AI will auto-split it into multiple lessons."
                        >
                            <Wand2 className="h-3.5 w-3.5" />
                            <span className="text-xs font-semibold">
                                AI: Split one PDF into lessons
                            </span>
                        </Button>
                        {(chapter.lessons || []).some(
                            (l) =>
                                (l.attachments || []).some(
                                    (a) => (a.content_type || "").toLowerCase() === "application/pdf",
                                ) && l.content_type !== "slideshow",
                        ) ? (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onConvertAllInChapter(chapter.id, chapter.title)}
                                className="gap-1.5 border-primary text-primary hover:bg-primary/10"
                                data-testid={`convert-all-top-${chapter.id}`}
                            >
                                <Presentation className="h-3.5 w-3.5" />
                                <span className="text-xs font-semibold">
                                    Convert all to slideshow
                                </span>
                            </Button>
                        ) : null}
                        {lessons.length > 0 ? (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onRenderAll(chapter)}
                                className="gap-1.5 border-primary text-primary hover:bg-primary/10"
                                data-testid={`render-all-top-${chapter.id}`}
                                title="Open per-lesson storyboards, edit slides, and render videos for all lessons in this chapter."
                            >
                                <Film className="h-3.5 w-3.5" />
                                <span className="text-xs font-semibold">
                                    Storyboards &amp; videos
                                </span>
                            </Button>
                        ) : null}
                        {lessons.length > 0 ? (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onPptxAll(chapter)}
                                className="gap-1.5 border-accent text-accent hover:bg-accent/10"
                                data-testid={`pptx-all-top-${chapter.id}`}
                                title="Generate a branded PowerPoint deck (.pptx) for every lesson in this chapter."
                            >
                                <Presentation className="h-3.5 w-3.5" />
                                <span className="text-xs font-semibold">
                                    PPTX for all lessons
                                </span>
                            </Button>
                        ) : null}
                        {lessons.length > 0 ? (
                            <CombinedChapterPptxButton
                                chapter={chapter}
                                testIdSuffix="top"
                            />
                        ) : null}
                    </div>
                    <div className="space-y-2">
                        {lessons.length === 0 && (
                            <div className="text-sm text-muted-foreground italic py-2">
                                No lessons yet.
                            </div>
                        )}
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleLessonDragEnd}
                        >
                            <SortableContext
                                items={lessons.map((l) => l.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                {lessons.map((l, lIdx) => (
                                    <SortableLesson
                                        key={l.id}
                                        lesson={l}
                                        index={lIdx}
                                        chapterId={chapter.id}
                                        chapterTitle={chapter.title}
                                        onEdit={onEditLesson}
                                        onDelete={onDeleteLesson}
                                        onGenSlides={onGenSlides}
                                        onConvert={onConvertLesson}
                                        slidesLoading={slidesLoading}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>

                        {/* Quiz row */}
                        <div
                            className="flex items-center gap-3 p-3 border border-border border-dashed rounded-sm bg-background"
                            data-testid={`chapter-quiz-${chapter.id}`}
                        >
                            <HelpCircle className="h-4 w-4 text-accent flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-foreground">
                                    Chapter quiz
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {chapter.has_quiz
                                        ? `${chapter.quiz_question_count} question${chapter.quiz_question_count === 1 ? "" : "s"} · required for progression`
                                        : "No quiz yet — students can skip this chapter"}
                                </div>
                            </div>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onEditQuiz(chapter.id, chapter.title)}
                                data-testid={`chapter-quiz-edit-${chapter.id}`}
                            >
                                <Pencil className="h-3 w-3 mr-1" />
                                {chapter.has_quiz ? "Edit quiz" : "Add quiz"}
                            </Button>
                        </div>
                    </div>

                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onNewLesson(chapter.id, chapter.title)}
                            data-testid={`add-lesson-${chapter.id}`}
                        >
                            <Plus className="h-3 w-3 mr-1" />
                            Add lesson
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => onBulkImport(chapter.id, chapter.title)}
                            className="ai-shimmer gap-1.5"
                            data-testid={`bulk-import-${chapter.id}`}
                        >
                            <Sparkles className="h-3.5 w-3.5" />
                            <span className="text-xs font-semibold">
                                Import lessons from PDFs
                            </span>
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onAiSplit(chapter.id, chapter.title)}
                            className="gap-1.5 border-accent text-accent-foreground hover:bg-accent/15"
                            data-testid={`ai-split-${chapter.id}`}
                            title="Upload ONE chapter PDF — AI will auto-split it into multiple lessons."
                        >
                            <Wand2 className="h-3.5 w-3.5" />
                            <span className="text-xs font-semibold">
                                AI: Split one PDF into lessons
                            </span>
                        </Button>
                        {(chapter.lessons || []).some(
                            (l) =>
                                (l.attachments || []).some(
                                    (a) => (a.content_type || "").toLowerCase() === "application/pdf",
                                ) && l.content_type !== "slideshow",
                        ) ? (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onConvertAllInChapter(chapter.id, chapter.title)}
                                className="gap-1.5"
                                data-testid={`convert-all-${chapter.id}`}
                            >
                                <Presentation className="h-3.5 w-3.5" />
                                <span className="text-xs font-semibold">
                                    Convert all to slideshow
                                </span>
                            </Button>
                        ) : null}
                        {lessons.length > 0 ? (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onRenderAll(chapter)}
                                className="gap-1.5 border-primary text-primary hover:bg-primary/10"
                                data-testid={`render-all-${chapter.id}`}
                                title="Open per-lesson storyboards, edit slides, and render videos for all lessons in this chapter."
                            >
                                <Film className="h-3.5 w-3.5" />
                                <span className="text-xs font-semibold">
                                    Storyboards &amp; videos
                                </span>
                            </Button>
                        ) : null}
                        {lessons.length > 0 ? (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onPptxAll(chapter)}
                                className="gap-1.5 border-accent text-accent hover:bg-accent/10"
                                data-testid={`pptx-all-${chapter.id}`}
                                title="Generate a branded PowerPoint deck (.pptx) for every lesson in this chapter."
                            >
                                <Presentation className="h-3.5 w-3.5" />
                                <span className="text-xs font-semibold">
                                    PPTX for all lessons
                                </span>
                            </Button>
                        ) : null}
                        {lessons.length > 0 ? (
                            <CombinedChapterPptxButton chapter={chapter} />
                        ) : null}
                    </div>
                </AccordionContent>
            </AccordionItem>
        </div>
    );
}

export default function CurriculumBuilder({ program, onRefresh, onAIGenerate, generating }) {
    const [chapterDialog, setChapterDialog] = useState({ open: false, chapter: null });
    const [lessonDialog, setLessonDialog] = useState({ open: false, chapterId: null, chapterTitle: null, lesson: null });
    const [quizDialog, setQuizDialog] = useState({ open: false, chapterId: null, chapterTitle: "" });
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [slidesLoading, setSlidesLoading] = useState({});
    const [outlineGenOpen, setOutlineGenOpen] = useState(false);
    const [bulkImport, setBulkImport] = useState({ open: false, chapterId: null, chapterTitle: null });
    const [aiSplit, setAiSplit] = useState({ open: false, chapterId: null, chapterTitle: null });
    const [convertDialog, setConvertDialog] = useState({ open: false, lesson: null });
    const [convertAll, setConvertAll] = useState({ open: false, chapterId: null, chapterTitle: null });
    const [renderAll, setRenderAll] = useState({ open: false, chapterId: null, chapterTitle: null });
    const [pptxAll, setPptxAll] = useState({ open: false, chapterId: null, chapterTitle: null, lessons: [] });

    const openConvert = (lesson) => setConvertDialog({ open: true, lesson });
    const openConvertAll = (chapterId, chapterTitle) =>
        setConvertAll({ open: true, chapterId, chapterTitle });
    const openRenderAll = (chapter) =>
        setRenderAll({
            open: true,
            chapterId: chapter.id,
            chapterTitle: chapter.title,
        });
    const openPptxAll = (chapter) =>
        setPptxAll({
            open: true,
            chapterId: chapter.id,
            chapterTitle: chapter.title,
            lessons: chapter.lessons || [],
        });

    // Local override for optimistic reordering
    const [overrideChapters, setOverrideChapters] = useState(null);

    const baseChapters = program.chapters || [];
    const chapters = overrideChapters || baseChapters;
    const nextChapterOrder = chapters.length;

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    // Reset override when fresh data arrives
    React.useEffect(() => {
        setOverrideChapters(null);
    }, [baseChapters]);

    // Refresh the chapter list when a backgrounded lesson-video render
    // completes — fired by AiJobsTray jobs registered from anywhere in the
    // app so the "video attached" badge / Re-render CTA appears even if
    // the user closed the lesson dialog before the MP4 was ready.
    React.useEffect(() => {
        const handler = () => { onRefresh?.(); };
        window.addEventListener("cco:lesson-video-rendered", handler);
        window.addEventListener("cco:lesson-pptx-generated", handler);
        return () => {
            window.removeEventListener("cco:lesson-video-rendered", handler);
            window.removeEventListener("cco:lesson-pptx-generated", handler);
        };
    }, [onRefresh]);

    const openNewChapter = () => setChapterDialog({ open: true, chapter: null });
    const openEditChapter = (ch) => setChapterDialog({ open: true, chapter: ch });
    const openNewLesson = (chapterId, chapterTitle) =>
        setLessonDialog({ open: true, chapterId, chapterTitle, lesson: null });
    const openEditLesson = (chapterId, lesson, chapterTitle) =>
        setLessonDialog({ open: true, chapterId, chapterTitle, lesson });
    const openBulkImport = (chapterId, chapterTitle) =>
        setBulkImport({ open: true, chapterId, chapterTitle });
    const openAiSplit = (chapterId, chapterTitle) =>
        setAiSplit({ open: true, chapterId, chapterTitle });
    const openQuiz = (chapterId, chapterTitle) =>
        setQuizDialog({ open: true, chapterId, chapterTitle });

    const askDeleteChapter = (ch) =>
        setConfirmDelete({ kind: "chapter", id: ch.id, label: ch.title });
    const askDeleteLesson = (l) =>
        setConfirmDelete({ kind: "lesson", id: l.id, label: l.title });

    const runDelete = async () => {
        if (!confirmDelete) return;
        try {
            if (confirmDelete.kind === "chapter") {
                await api.delete(`/chapters/${confirmDelete.id}`);
                toast.success("Chapter deleted");
            } else {
                await api.delete(`/lessons/${confirmDelete.id}`);
                toast.success("Lesson deleted");
            }
            setConfirmDelete(null);
            onRefresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
            setConfirmDelete(null);
        }
    };

    const generateSlides = async (lessonId) => {
        setSlidesLoading((s) => ({ ...s, [lessonId]: true }));
        try {
            const { data } = await api.post(`/lessons/${lessonId}/generate-slides`);
            toast.success("Slides generated");
            window.open(
                `${API}/slides/${data.id}/download?auth=${localStorage.getItem("token")}`,
                "_blank",
            );
            onRefresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Slide generation failed");
        } finally {
            setSlidesLoading((s) => ({ ...s, [lessonId]: false }));
        }
    };

    const chapterIds = useMemo(() => chapters.map((c) => c.id), [chapters]);

    const handleChapterDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIdx = chapters.findIndex((c) => c.id === active.id);
        const newIdx = chapters.findIndex((c) => c.id === over.id);
        if (oldIdx < 0 || newIdx < 0) return;
        const reordered = arrayMove(chapters, oldIdx, newIdx);
        setOverrideChapters(reordered);
        try {
            await api.put(`/programs/${program.id}/chapters/reorder`, {
                order: reordered.map((c) => c.id),
            });
            onRefresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reorder failed");
            setOverrideChapters(null);
        }
    };

    const handleReorderLessons = async (chapterId, newOrder) => {
        // Optimistic update
        setOverrideChapters(
            chapters.map((c) =>
                c.id === chapterId
                    ? {
                          ...c,
                          lessons: newOrder.map((id) => c.lessons.find((l) => l.id === id)),
                      }
                    : c,
            ),
        );
        try {
            await api.put(`/chapters/${chapterId}/lessons/reorder`, { order: newOrder });
            onRefresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reorder failed");
            setOverrideChapters(null);
        }
    };

    return (
        <div className="space-y-6" data-testid="curriculum-builder">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display text-2xl text-primary">Curriculum</h2>
                    <p className="text-sm text-muted-foreground">
                        Organise your program into chapters, lessons and quizzes. Drag the grip
                        icon to reorder.
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button
                        variant="outline"
                        onClick={() => setOutlineGenOpen(true)}
                        data-testid="outline-generate-open-btn"
                    >
                        <Sparkles className="h-4 w-4 mr-2" />
                        Generate from outline
                    </Button>
                    <Button
                        variant="outline"
                        onClick={onAIGenerate}
                        disabled={generating}
                        data-testid="ai-generate-btn"
                    >
                        <Sparkles className="h-4 w-4 mr-2" />
                        {generating ? "Generating…" : "Quick AI draft"}
                    </Button>
                    <Button
                        onClick={openNewChapter}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="add-chapter-btn"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Add chapter
                    </Button>
                </div>
            </div>

            {chapters.length === 0 ? (
                <Card className="border-dashed border-border">
                    <CardContent className="p-10 text-center">
                        <div className="font-display text-lg text-primary mb-1">
                            Start building your curriculum
                        </div>
                        <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                            Add your first chapter, or let AI draft the structure for you.
                            You can edit anything afterwards.
                        </p>
                        <div className="flex gap-2 justify-center flex-wrap">
                            <Button
                                onClick={openNewChapter}
                                className="bg-primary hover:bg-primary/90"
                                data-testid="add-chapter-empty-btn"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Add chapter
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => setOutlineGenOpen(true)}
                                data-testid="outline-generate-empty-btn"
                            >
                                <Sparkles className="h-4 w-4 mr-2" />
                                Generate from outline
                            </Button>
                            <Button variant="outline" onClick={onAIGenerate} disabled={generating}>
                                <Sparkles className="h-4 w-4 mr-2" />
                                Quick AI draft
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleChapterDragEnd}
                >
                    <SortableContext items={chapterIds} strategy={verticalListSortingStrategy}>
                        <Accordion
                            type="multiple"
                            defaultValue={chapterIds}
                            className="space-y-3"
                        >
                            {chapters.map((ch, idx) => (
                                <SortableChapter
                                    key={ch.id}
                                    chapter={ch}
                                    index={idx}
                                    onEditChapter={openEditChapter}
                                    onDeleteChapter={askDeleteChapter}
                                    onNewLesson={openNewLesson}
                                    onEditLesson={openEditLesson}
                                    onDeleteLesson={askDeleteLesson}
                                    onGenSlides={generateSlides}
                                    onEditQuiz={openQuiz}
                                    onBulkImport={openBulkImport}
                                    onAiSplit={openAiSplit}
                                    onConvertLesson={openConvert}
                                    onConvertAllInChapter={openConvertAll}
                                    onRenderAll={openRenderAll}
                                    onPptxAll={openPptxAll}
                                    slidesLoading={slidesLoading}
                                    sensors={sensors}
                                    onReorderLessons={handleReorderLessons}
                                />
                            ))}
                        </Accordion>
                    </SortableContext>
                </DndContext>
            )}

            {/* Dialogs */}
            <ChapterDialog
                open={chapterDialog.open}
                onOpenChange={(v) => setChapterDialog((s) => ({ ...s, open: v }))}
                programId={program.id}
                chapter={chapterDialog.chapter}
                defaultOrder={nextChapterOrder}
                onSaved={onRefresh}
            />
            <LessonDialog
                open={lessonDialog.open}
                onOpenChange={(v) => setLessonDialog((s) => ({ ...s, open: v }))}
                chapterId={lessonDialog.chapterId}
                chapterTitle={lessonDialog.chapterTitle}
                programId={program.id}
                lesson={lessonDialog.lesson}
                onSaved={onRefresh}
            />
            <QuizDialog
                open={quizDialog.open}
                onOpenChange={(v) => setQuizDialog((s) => ({ ...s, open: v }))}
                chapterId={quizDialog.chapterId}
                chapterTitle={quizDialog.chapterTitle}
                onSaved={onRefresh}
            />

            <OutlineGenerateDialog
                open={outlineGenOpen}
                onOpenChange={setOutlineGenOpen}
                programId={program.id}
                existingOutline={
                    program.outline_filename
                        ? {
                              filename: program.outline_filename,
                              preview:
                                  "Outline already uploaded. You may keep it or upload a new one.",
                          }
                        : null
                }
                hasExistingChapters={chapters.length > 0}
                onCompleted={onRefresh}
            />

            <AlertDialog
                open={!!confirmDelete}
                onOpenChange={(o) => !o && setConfirmDelete(null)}
            >
                <AlertDialogContent data-testid="curriculum-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display">
                            Delete {confirmDelete?.kind === "chapter" ? "chapter" : "lesson"}?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-medium text-foreground">
                                "{confirmDelete?.label}"
                            </span>{" "}
                            and all nested content will be removed. Student progress on these
                            items will also be cleared. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={runDelete}
                            className="bg-destructive hover:bg-destructive/90"
                            data-testid="curriculum-delete-confirm"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <BulkImportPdfDialog
                open={bulkImport.open}
                onOpenChange={(v) => setBulkImport((s) => ({ ...s, open: v }))}
                chapterId={bulkImport.chapterId}
                chapterTitle={bulkImport.chapterTitle}
                onDone={onRefresh}
            />
            <AiSplitPdfDialog
                open={aiSplit.open}
                onOpenChange={(v) => setAiSplit((s) => ({ ...s, open: v }))}
                chapterId={aiSplit.chapterId}
                chapterTitle={aiSplit.chapterTitle}
                onDone={onRefresh}
            />
            <RenderAllVideosDialog
                open={renderAll.open}
                onOpenChange={(v) => setRenderAll((s) => ({ ...s, open: v }))}
                chapterId={renderAll.chapterId}
                chapterTitle={renderAll.chapterTitle}
                lessons={
                    (chapters.find((c) => c.id === renderAll.chapterId)?.lessons) || []
                }
                onRefresh={onRefresh}
            />
            <RenderAllPptxDialog
                open={pptxAll.open}
                onOpenChange={(v) => setPptxAll((s) => ({ ...s, open: v }))}
                chapterId={pptxAll.chapterId}
                chapterTitle={pptxAll.chapterTitle}
                lessons={
                    (chapters.find((c) => c.id === pptxAll.chapterId)?.lessons) || []
                }
                onAnyGenerated={onRefresh}
            />
            <ConvertPdfDialog
                open={convertDialog.open}
                onOpenChange={(v) => setConvertDialog((s) => ({ ...s, open: v }))}
                lessonId={convertDialog.lesson?.id}
                lessonTitle={convertDialog.lesson?.title}
                onDone={onRefresh}
            />
            <ChapterConvertAllDialog
                open={convertAll.open}
                onOpenChange={(v) => setConvertAll((s) => ({ ...s, open: v }))}
                chapterId={convertAll.chapterId}
                chapterTitle={convertAll.chapterTitle}
                onDone={onRefresh}
            />
        </div>
    );
}
